"use client";

import Image from "next/image";
import { useCallback } from "react";
import type { Persona } from "@/lib/api";

function StarRating({ rating }: { rating: number }) {
  return (
    <span
      className="flex items-center gap-1 text-[12px]"
      style={{ color: "var(--ish-text-muted)" }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="#f59e0b" aria-hidden>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
      {rating.toFixed(1)}
    </span>
  );
}

function trackJyotishiCardTap(properties: Record<string, unknown>) {
  void import("@/lib/analytics-events").then(({ trackEvent }) => {
    trackEvent("jyotishi_card_to_form", properties);
  });
}

/**
 * Persona card on /ai-astrologer/. Visually it reads as a selectable astrologer
 * card (avatar + rating + verified badge), so Microsoft Clarity showed users
 * tapping it expecting to start a consultation — the dominant dead-click source
 * on the page (rule 32). Per ADR-0021 there is ONE Jyotishi and no persona
 * picker, and rule 39 forbids a competing CTA, so the tap must route to the
 * page's single job, not a second offer: it scrolls to + focuses the
 * birth-details form (the hero `#start-reading`), turning a frustrated dead tap
 * into forward progress. The dead-click rate on `/ai-astrologer/` is the
 * verification signal (the morning friction digest line).
 */
export default function JyotishiCard({ jyotishi }: { jyotishi: Persona; position?: number }) {
  const startReading = useCallback(() => {
    if (typeof document === "undefined") return;
    const form = document.getElementById("start-reading");
    if (!form) return;
    form.scrollIntoView({ behavior: "smooth", block: "start" });
    const field = form.querySelector<HTMLElement>("input, select, textarea");
    if (field) window.setTimeout(() => field.focus({ preventScroll: true }), 350);
    trackJyotishiCardTap({ jyotishi_id: jyotishi.id, source_page: "/ai-astrologer/" });
  }, [jyotishi.id]);

  return (
    <button
      type="button"
      onClick={startReading}
      aria-label={`Start your free reading with ${jyotishi.name}`}
      className="group flex h-full w-full flex-col items-start gap-4 rounded-xl p-4 text-left transition-shadow hover:shadow-[0_8px_24px_rgba(36,24,18,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ish-accent)]"
      style={{
        background: "var(--ish-bg)",
        border: "1px solid var(--ish-border)",
        cursor: "pointer",
      }}
    >
      <div className="flex w-full items-start gap-3">
        <div className="relative shrink-0">
          {jyotishi.avatar_url ? (
            <Image
              src={jyotishi.avatar_url}
              alt={jyotishi.name}
              width={64}
              height={64}
              sizes="64px"
              className="rounded-full object-cover"
              style={{ border: "2px solid var(--ish-accent)", width: 64, height: 64 }}
            />
          ) : (
            <div
              className="flex rounded-full items-center justify-center"
              style={{
                width: 64,
                height: 64,
                border: "2px solid var(--ish-accent)",
                background: "var(--ish-surface)",
                color: "var(--ish-accent)",
                fontFamily: "var(--font-pt-serif)",
                fontSize: 22,
                fontWeight: 700,
              }}
              aria-hidden
            >
              {jyotishi.name.charAt(0)}
            </div>
          )}
          <span
            className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px]"
            style={{ background: "var(--ish-accent)", color: "#fff" }}
            aria-hidden
          >
            ✓
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-bold leading-snug" style={{ color: "var(--ish-dark)" }}>
            {jyotishi.name}
          </p>
          <p className="mt-0.5 text-[12px] font-semibold" style={{ color: "var(--ish-accent)" }}>
            {jyotishi.category}
          </p>
          <div className="mt-1">
            <StarRating rating={jyotishi.rating} />
          </div>
        </div>
      </div>
      {jyotishi.lineage_text ? (
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
          {jyotishi.lineage_text}
        </p>
      ) : null}
    </button>
  );
}
